import { hashKey, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StrictMode, useSyncExternalStore } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { ErroApi, operacao, token } from './api/cliente';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { VisaoGeral } from './pages/VisaoGeral';
import './estilo.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: false,
      // 4xx não melhora repetindo; 5xx e rede tentam mais duas vezes
      // cada operação tem o próprio cache: trocar no switch não mostra número da outra
      queryKeyHashFn: (chave) => hashKey([operacao.ler() ?? '', ...chave]),
      retry: (n, err) => !(err instanceof ErroApi && err.status < 500) && !(err instanceof ErroApi && err.status === 503) && n < 2,
    },
  },
});

const router = createBrowserRouter([
  {
    element: <Layout />,
    children: [
      { path: '/', element: <VisaoGeral /> },
      // abas com gráfico carregam o Recharts só quando abertas
      { path: '/pagamentos', lazy: async () => ({ Component: (await import('./pages/Pagamentos')).Pagamentos }) },
      { path: '/afiliados', lazy: async () => ({ Component: (await import('./pages/Afiliados')).Afiliados }) },
      { path: '/apostas', lazy: async () => ({ Component: (await import('./pages/Apostas')).Apostas }) },
      { path: '/risco', lazy: async () => ({ Component: (await import('./pages/Risco')).Risco }) },
      { path: '/risco/evento', lazy: async () => ({ Component: (await import('./pages/Evento')).Evento }) },
      { path: '/cassino', lazy: async () => ({ Component: (await import('./pages/Cassino')).Cassino }) },
      { path: '/niveis', lazy: async () => ({ Component: (await import('./pages/Niveis')).Niveis }) },
      { path: '/saques', lazy: async () => ({ Component: (await import('./pages/Saques')).Saques }) },
      { path: '/jogadores', lazy: async () => ({ Component: (await import('./pages/Jogadores')).Jogadores }) },
      { path: '/conteudo', lazy: async () => ({ Component: (await import('./pages/Conteudo')).Conteudo }) },
      { path: '/acessos', lazy: async () => ({ Component: (await import('./pages/Acessos')).Acessos }) },
      { path: '/dados', lazy: async () => ({ Component: (await import('./pages/Dados')).Dados }) },
      { path: '*', element: <VisaoGeral /> },
    ],
  },
]);

const assinar = (evento: string) => (cb: () => void) => {
  window.addEventListener(evento, cb);
  window.addEventListener('storage', cb);
  return () => {
    window.removeEventListener(evento, cb);
    window.removeEventListener('storage', cb);
  };
};
const assinarToken = assinar('bi-evox:token');
const assinarOperacao = assinar('bi-evox:operacao');

function Raiz() {
  const t = useSyncExternalStore(assinarToken, token.ler);
  // trocar de operação remonta as telas: cada consulta passa a usar a chave da operação nova
  const op = useSyncExternalStore(assinarOperacao, operacao.ler);
  if (!t) {
    queryClient.clear();
    return <Login />;
  }
  return <RouterProvider key={op ?? ''} router={router} />;
}

createRoot(document.getElementById('raiz') as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Raiz />
      {import.meta.env.DEV && <ReactQueryDevtools buttonPosition="bottom-left" />}
    </QueryClientProvider>
  </StrictMode>,
);
